import { useState } from "react";
import { Link } from "react-router";
import { ROUTES } from "../links";
import { usePageTitle } from "./usePageTitle";
import { PageFaq } from "../widgets/PageFaq";

/** Photos are copied from wealthyplanet.com/team/ into public/team/ (resized, metadata removed). */
const TEAM: [name: string, role: string, bio: string, photo: string][] = [
  [
    "John Podlewski",
    "Founder & CEO",
    "30+ years financial services; previously built and exited a fintech platform.",
    "john.jpg",
  ],
  [
    "Dr. Stephen Watt",
    "Advisory chair",
    "Former Dean of Mathematics; Maplesoft co-founder; AI governance expert.",
    "stephen.jpg",
  ],
  [
    "Andrei Muresanu",
    "Co-founder & Abi architect",
    "NeurIPS-recognised AI researcher at Vector Institute and NVIDIA.",
    "andrei.jpg",
  ],
  [
    "Eric Polo",
    "Co-founder, COO & head of product",
    "Former CIBC; fintech leader focused on product execution and go-to-market readiness.",
    "eric.jpg",
  ],
  [
    "Mustansir Soni",
    "VP front-end engineering",
    "25+ years experience in growth, sales and channel development.",
    "mustansir.jpg",
  ],
  [
    "Courtney Podlewski",
    "Co-founder & VP investor relations",
    "20 years C-suite communications, investor relations and fundraising leadership.",
    "courtney.jpg",
  ],
  [
    "Ian Watt",
    "SVP of technology",
    "Leads semantic interface, correctness validation and algorithm testing.",
    "ian.jpg",
  ],
  [
    "Tanavya Dimri",
    "VP of backend engineering",
    "Founding engineer; focused on platform architecture and system reliability.",
    "tanavya.jpg",
  ],
  [
    "Alex",
    "Machine learning engineer",
    "Optimising and extending the financial simulation engine.",
    "alex.png",
  ],
];

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter((w) => !/^(dr\.?)$/i.test(w))
    .slice(0, 2)
    .map((w) => w[0])
    .join("");
}

/** A photo that falls back to the person's initials if it fails to load. */
function Photo({ name, file }: { name: string; file: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <span className="tph">
      {failed ? (
        <span className="tini">{initials(name)}</span>
      ) : (
        <img
          src={`/team/${file}`}
          alt={name.replace(/&/g, "and")}
          loading="lazy"
          onError={() => setFailed(true)}
        />
      )}
    </span>
  );
}

/** Our team (the prototype's `data-s="about"`). */
export function TeamPage() {
  usePageTitle("Our team");
  return (
    <div className="spage on" data-s="about">
      <header className="hero" style={{ paddingBottom: 0 }}>
        <div className="wrap">
          <h1>
            A vision built on <em>clarity</em>.
          </h1>
          <p className="lede">
            Financial decisions are personal, complex and high-stakes. Our team combines decades of
            financial services expertise with world-class AI research.
          </p>
        </div>
      </header>
      <section className="band">
        <div className="wrap" style={{ maxWidth: 780 }}>
          <div className="card tall">
            <h3 style={{ fontSize: 28, marginBottom: 16 }}>Why we built it</h3>
            <p style={{ fontSize: 15.5, lineHeight: 1.72, marginBottom: 16 }}>
              Good financial planning has always existed. It has just never been affordable for the
              people who need it most. A planner who models your tax, your mortgage and your
              retirement properly costs thousands of dollars a year, which means it&rsquo;s been
              reserved for households that already have enough money to be relaxed about it.
            </p>
            <p style={{ fontSize: 15.5, lineHeight: 1.72, marginBottom: 16 }}>
              The work itself, though, is arithmetic. Enormous amounts of it, across decades, with
              every piece touching every other piece. That&rsquo;s precisely the kind of problem a
              computer should be doing, and until recently nobody had built one that could.
            </p>
            <p style={{ fontSize: 15.5, lineHeight: 1.72 }}>
              So we built Abi. It does for ten dollars a month roughly what a good planner does, for
              the households that were never going to get one.
            </p>
          </div>
        </div>
      </section>

      <section className="band teamband">
        <div className="wrap">
          <div className="shead" style={{ marginBottom: 34 }}>
            <p className="mono">People behind the vision</p>
            <h2>Who&rsquo;s behind it</h2>
          </div>
          <div className="team" id="site-team">
            {TEAM.map(([name, role, bio, file]) => (
              <div className="tmem" key={name}>
                <Photo name={name} file={file} />
                <b>{name}</b>
                <p className="mono">{role}</p>
                <p>{bio}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 36 }}>
            <Link className="sbtn" to={ROUTES.contact}>
              Get in touch
            </Link>
          </div>
        </div>
      </section>
      <PageFaq page="about" />
    </div>
  );
}
